// Il dettaglio di un progetto, scheda «Piano»: requisiti, team di pops e fasi, gli stessi editor
// della procedura «Nuovo progetto». Il salvataggio cambia il piano; le attività già create
// tengono il compito di quando sono nate.
import { useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import { Field, TextArea } from '../../components/Field';
import { ApiError, api } from '../../lib/api';
import type {
  AgentProfile,
  ConnectedProvider,
  Pipeline,
  Project,
  ProjectPlan,
} from '../../lib/api';
import { useStore } from '../../store/StoreProvider';
import { PhasesEditor } from './new/PhasesEditor';
import { EMPTY_PLAN } from './new/plan';
import { TeamEditor } from './new/TeamEditor';

export function ProjectPlanTab({
  project,
  onSaved,
}: {
  project: Project;
  onSaved: (p: Project) => void;
}) {
  const { toast } = useStore();
  const [plan, setPlan] = useState<ProjectPlan>(project.plan ?? EMPTY_PLAN);
  const [profiles, setProfiles] = useState<AgentProfile[]>([]);
  const [providers, setProviders] = useState<ConnectedProvider[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<ProjectPlan>) => setPlan((p) => ({ ...p, ...patch }));

  useEffect(() => {
    void api.profiles().then(setProfiles, () => setProfiles([]));
    void api.pipelines().then(setPipelines, () => setPipelines([]));
    void api.providers().then(
      (p) => setProviders(p.providers),
      () => setProviders([]),
    );
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const saved = await api.saveProject(project.id, { ...project, plan });
      toast({ kind: 'success', title: `${saved.name}: piano salvato` });
      onSaved(saved);
    } catch (e) {
      const body = e instanceof ApiError && e.detail ? e.detail : 'Riprova fra poco.';
      toast({ kind: 'danger', title: 'Piano non salvato', body });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="np-page">
      <section className="np-card">
        <Field label="Requisiti" hint="In Markdown: PM e Team leader li leggono a ogni avvio.">
          <TextArea
            value={plan.requirements}
            onChange={(requirements) => set({ requirements })}
            rows={12}
            mono
          />
        </Field>
      </section>
      <TeamEditor
        team={plan.team}
        onChange={(team) => set({ team })}
        profiles={profiles}
        providers={providers}
        phases={plan.phases.map((p) => p.name).filter(Boolean)}
      />
      <PhasesEditor
        plan={plan}
        onChange={set}
        team={plan.team}
        providers={providers}
        pipelines={pipelines}
      />
      <div className="np-actions" style={{ justifyContent: 'flex-end' }}>
        <Button variant="primary" loading={busy} onClick={() => void save()}>
          Salva il piano
        </Button>
      </div>
    </div>
  );
}
